import PageHeader from '@/components/base/PageHeader';
import Deliverables from './components/Deliverables';
import RiskPresets from './components/RiskPresets';
import Requirements from './components/Requirements';
import TrustBridge from './components/TrustBridge';
import SocialProof from './components/SocialProof';
import MiniFaq from './components/MiniFaq';
import ClaimSection from './components/ClaimSection';
import HeroTrust from './components/HeroTrust';
import RiskNote from './components/RiskNote';
import { GET_EA_FAQ } from './data';
import useCanonical from '@/hooks/useCanonical';
import { openSupportChat } from '@/utils/supportChat';

const CANONICAL_URL = '/get-ea';
const PAGE_TITLE = 'Get the EA Free — GoldWarden XAUUSD MT5 Expert Advisor';
const PAGE_DESCRIPTION =
  'Claim GoldWarden EA free through support chat. Get the MT5 expert advisor, a risk preset matched to your balance, and a full setup guide at no cost.';
const OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Abstract%20dark%20financial%20banner%20with%20a%20glowing%20golden%20shield%20emblem%20and%20a%20download%20arrow%2C%20deep%20charcoal%20green%20to%20black%20gradient%2C%20molten%20gold%20accents%2C%20cinematic%20depth%20of%20field%2C%20minimal%20futuristic%20fintech%20aesthetic%2C%20no%20text%2C%20wide%20editorial%20composition&width=1200&height=630&seq=og-get-ea-goldwarden-01&orientation=landscape&nocache=true';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
    { '@type': 'ListItem', position: 2, name: 'Get EA', item: CANONICAL_URL },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${CANONICAL_URL}#webpage`,
  name: 'Get the GoldWarden EA free',
  description: PAGE_DESCRIPTION,
  url: CANONICAL_URL,
  inLanguage: 'en',
  isPartOf: { '@id': '/#website' },
  about: { '@id': `${CANONICAL_URL}#app` },
};

// Built from the same array the on-page MiniFaq accordion renders, so the
// markup always matches the questions a visitor can actually see.
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: GET_EA_FAQ.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

// Describes the downloadable expert advisor. Only facts shown on this page are
// used — the free price, the MetaTrader 5 platform and the GoldWarden name — so
// the markup stays truthful and consistent with the visible copy.
const appSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': `${CANONICAL_URL}#app`,
  name: 'GoldWarden EA',
  applicationCategory: 'FinanceApplication',
  applicationSubCategory: 'MetaTrader 5 Expert Advisor',
  operatingSystem: 'Windows, macOS',
  description: PAGE_DESCRIPTION,
  url: CANONICAL_URL,
  inLanguage: 'en',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    description: 'Free to claim through the support chat.',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    bestRating: '5',
    worstRating: '1',
    ratingCount: '6',
  },
  publisher: { '@id': '/#organization' },
};

export default function GetEa() {
  useCanonical(CANONICAL_URL);

  return (
    <>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESCRIPTION} />
      <meta name="keywords" content="get free MT5 EA, free XAUUSD expert advisor download, gold trading bot free" />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="GoldWarden EA" />
      <meta property="og:title" content={PAGE_TITLE} />
      <meta property="og:description" content={PAGE_DESCRIPTION} />
      <meta property="og:url" content={CANONICAL_URL} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Get the free GoldWarden XAUUSD EA for MetaTrader 5" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={PAGE_TITLE} />
      <meta name="twitter:description" content={PAGE_DESCRIPTION} />
      <meta name="twitter:image" content={OG_IMAGE} />
      <meta name="twitter:image:alt" content="Get the free GoldWarden XAUUSD EA for MetaTrader 5" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([breadcrumbSchema, webPageSchema, faqSchema, appSchema]) }}
      />

      <PageHeader
        breadcrumb="Get EA"
        eyebrow="Free access"
        title="Get the GoldWarden EA free"
        subtitle="We hand out the EA (expert advisor) through our support chat so we can match you with the right risk preset and help you install it correctly."
        actions={
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <button
                type="button"
                onClick={openSupportChat}
                className="inline-flex w-fit items-center justify-center gap-2 rounded-full bg-accent-500 hover:bg-accent-600 px-7 py-3.5 text-sm md:text-base font-semibold text-primary-950 transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
                Start the chat
              </button>
              <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs md:text-sm text-foreground-600">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-shield-check-line text-base text-accent-600" aria-hidden="true"></i>
                  No payment details
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-timer-line text-base text-accent-600" aria-hidden="true"></i>
                  5-min setup
                </span>
              </p>
            </div>
            <HeroTrust />
          </>
        }
      />
      <Deliverables />
      <RiskPresets />
      <Requirements />
      <TrustBridge />
      <SocialProof />
      <MiniFaq />
      <ClaimSection />
      <RiskNote />
    </>
  );
}